"use client";

import type React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/navbar";
import { Footer } from "@/components/footer";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Separator } from "../../components/ui/separator";
import { Badge } from "../../components/ui/badge";
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  AlertCircle,
  FileText,
  Users,
  Edit,
  Trash2,
  UserPlus,
  Mail,
  Phone,
  MapPin,
  Calendar,
  ShieldCheck,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { getAllBeneficiariosStorage } from "../lib/beneficiarios-storage";
import { apiClient } from "../lib/api";
import { formatRut } from "../lib/rut-validator";

import { useConsentStore } from "@/lib/stores/consentimiento-store";
import { useAuthStore } from "../lib/stores/auth-store";
import { usePolizasCacheStore } from "../lib/stores/polizas-cache-store";
import { useBeneficiariosStore } from "../../lib/stores/beneficiarios-store";

interface ErrorByRut {
  rut: string;
  errors: string[];
}

export default function ResumenPage() {
  const baseUrl = import.meta.env.VITE_BASE_URL;
  const navigate = useNavigate();
  const { asegurado, solicitudGuid } = useAuthStore();
  const { polizas: cachedPolizas } = usePolizasCacheStore();
  const [addedBeneficiarios, setAddedBeneficiarios] = useState<any[]>([]);
  const [modifiedBeneficiarios, setModifiedBeneficiarios] = useState<any[]>([]);
  const [deletedBeneficiarios, setDeletedBeneficiarios] = useState<any[]>([]);
  const [polizasWithChanges, setPolizasWithChanges] = useState<number[]>([]);
  const [polizasData, setPolizasData] = useState<any[]>([]);
  const [validation, setValidation] = useState<{
    isValid: boolean;
    invalidPolizas: Array<{ numero: number; total: number; faltante: number }>;
  }>({ isValid: true, invalidPolizas: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [errorList, setErrorList] = useState<ErrorByRut[]>([]);
  const [success, setSuccess] = useState(false);
  const [otp, setOtp] = useState("");
  const [rutContratante, setrutContratante] = useState(0);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const AceptaConsentimiento = useConsentStore(
    (state) => state.hasGivenConsent
  );

  useEffect(() => {
    const storage = getAllBeneficiariosStorage();

    if (
      storage.added.length === 0 &&
      storage.modified.length === 0 &&
      storage.deleted.length === 0
    ) {
      navigate(`${baseUrl}/polizas`);
      return;
    }

    setAddedBeneficiarios(storage.added);
    setModifiedBeneficiarios(storage.modified);
    setDeletedBeneficiarios(storage.deleted);

    const polizas = new Set<number>();
    storage.added.forEach((b: any) => polizas.add(b.numeroPoliza));
    storage.modified.forEach((b: any) => polizas.add(b.numeroPoliza));
    storage.deleted.forEach((b: any) => polizas.add(b.numeroPoliza));
    const polizasArray = Array.from(polizas);
    setPolizasWithChanges(polizasArray);

    if (cachedPolizas.length > 0) {
      setPolizasData(cachedPolizas);
      validatePercentages(cachedPolizas, storage, polizasArray);
    } else {
      if (!asegurado || !solicitudGuid) {
        return;
      }
      const fetchPolizas = async () => {
        const response = await apiClient.getPolizas(asegurado.rut);
        if (response.success && response.data) {
          setPolizasData(response.data);
          validatePercentages(response.data, storage, polizasArray);
        }
      };
      fetchPolizas();
    }
  }, [navigate, asegurado, solicitudGuid, cachedPolizas]);

  const validatePercentages = (
    polizas: any[],
    storage: any,
    polizasList: number[]
  ) => {
    const invalidPolizas: Array<{
      numero: number;
      total: number;
      faltante: number;
    }> = [];

    polizasList.forEach((numeroPoliza) => {
      const poliza = polizas.find((p) => p.numeroPoliza === numeroPoliza);
      if (!poliza) {
        return;
      }

      const rutContratante = obtenerRutPorPolizaId(numeroPoliza);
      setrutContratante(rutContratante || 0);
      const total = getTotalPercentageByPoliza(
        numeroPoliza,
        poliza.beneficiarios,
        storage
      );

      if (total !== 100 && total !== 0) {
        invalidPolizas.push({
          numero: numeroPoliza,
          total,
          faltante: 100 - total,
        });
      }
    });

    setValidation({
      isValid: invalidPolizas.length === 0,
      invalidPolizas,
    });
  };

  const getTotalBeneficiarios = (): number => {
    return addedBeneficiarios.length + modifiedBeneficiarios.length;
  };

  const getTotalActions = (): number => {
    return (
      addedBeneficiarios.length +
      modifiedBeneficiarios.length +
      deletedBeneficiarios.length
    );
  };

  const transformApiErrorsToRutGroups = (apiErrors: string[]): ErrorByRut[] => {
    const rutGroups: { [rut: string]: string[] } = {};

    apiErrors.forEach((error) => {
      const rutMatch = error.match(/RUT\s+([\d\-Kk]+)/);

      if (rutMatch) {
        const rut = rutMatch[1] || "";

        if (!rutGroups[rut]) {
          rutGroups[rut] = [];
        }

        // Opcional: Limpiar el texto removiendo el prefijo de póliza
        const cleanError = error.replace(/\[P6liza\s+\d+\]\s*/, "");
        rutGroups[rut].push(cleanError);
      }
    });

    return Object.entries(rutGroups).map(([rut, errors]) => ({
      rut,
      errors,
    }));
  };

  const getParentescoLabel = (parentesco: number) => {
    const labels: Record<number, string> = {
      5: "Cónyuge",
      6: "Hijo/a",
      7: "Padre",
      8: "Madre",
      9: "Hermano/a",
      10: "Otro",
    };
    return labels[parentesco] || "N/A";
  };

  const obtenerRutPorPolizaId = (numeroPoliza: number) => {
    const polizas = usePolizasCacheStore.getState().polizas;
    const poliza = polizas.find((p) => p.numeroPoliza === numeroPoliza);

    return poliza?.RutContratante;
  };

  const getTotalPercentageByPoliza = (
    numeroPoliza: number,
    beneficiarios: any[],
    storage?: any
  ) => {
    const addedList = storage ? storage.added : addedBeneficiarios;
    const modifiedList = storage ? storage.modified : modifiedBeneficiarios;
    const deletedList = storage ? storage.deleted : deletedBeneficiarios;

    const deletedRuts = deletedList
      .filter((b: any) => b.numeroPoliza === numeroPoliza)
      .map((b: any) => b.rut);

    const modifiedRuts = modifiedList
      .filter((b: any) => b.numeroPoliza === numeroPoliza)
      .map((b: any) => b.rut);

    const activeBeneficiarios = beneficiarios.filter(
      (b) => !deletedRuts.includes(b.rut) && !modifiedRuts.includes(b.rut)
    );

    // console.log(activeBeneficiarios);

    const storedBeneficiarios = [...addedList, ...modifiedList].filter(
      (b: any) => b.numeroPoliza === numeroPoliza
    );

    // console.log(storedBeneficiarios);

    const apiTotal = activeBeneficiarios.reduce(
      (total, b) => total + (b.porcentaje || 0),
      0
    );
    const storedTotal = storedBeneficiarios.reduce(
      (total: number, b: any) => total + (b.porcentaje || 0),
      0
    );
    const finalTotal = storedTotal + apiTotal;
    //VER EXCEPCION DE CASO cuando hay un beneficiario se calcula ese porcentaje demás
    // console.log(apiTotal, "apitOTAL");
    // console.log(storedTotal, "STOREEDF");
    // console.log(finalTotal, "ALO");

    return finalTotal;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setShowErrorDialog(false);
    setErrorMessage("");

    if (!validation.isValid) {
      setError(
        "No puedes finalizar la solicitud. Algunas pólizas no tienen el 100% de cobertura."
      );
      return;
    }

    if (!otp.trim()) {
      setError("Código de verificación requerido");
      return;
    }

    if (!asegurado || !solicitudGuid) {
      setError("Sesión inválida. Por favor, inicia sesión nuevamente.");
      return;
    }

    if (!otp) {
      setError("Por favor, ingresa el código de verificación (OTP).");
      return;
    }

    setLoading(true);

    const beneficiariosData = {
      solicitudGuid: solicitudGuid,
      AceptaConsentimiento: AceptaConsentimiento,
      RutContratante: rutContratante,
      Polizas: polizasWithChanges.map((numeroPoliza) => {
        const added = addedBeneficiarios.filter(
          (b) => b.numeroPoliza === numeroPoliza
        );
        const modified = modifiedBeneficiarios.filter(
          (b) => b.numeroPoliza === numeroPoliza
        );
        const deleted = deletedBeneficiarios.filter(
          (b) => b.numeroPoliza === numeroPoliza
        );

        const poliza = polizasData.find((p) => p.numeroPoliza === numeroPoliza);

        const apiBeneficiarios = poliza?.beneficiarios || [];

        const modifiedRuts = modified.map((b) => b.rut);
        const deletedRuts = deleted.map((b) => b.rut);

        const activeApiBeneficiarios = apiBeneficiarios
          .filter(
            (b: any) =>
              !deletedRuts.includes(b.rut) && !modifiedRuts.includes(b.rut)
          )
          .map((b: any) => ({
            tipoBeneficiario: b.tipoBeneficiario || "PRIMARIO",
            rut: b.rut,
            dv: b.dv,
            nombre: b.nombre,
            apellidoPaterno: b.apellidoPaterno,
            apellidoMaterno: b.apellidoMaterno,
            fechaNacimiento: b.fechaNacimiento,
            sexo: b.sexo || 0,
            parentesco: Number.parseInt(b.parentesco) || 0,
            email: b.email || "",
            region: b.idRegion || 0,
            ciudad: b.idCiudad || 0,
            comuna: b.idComuna || 0,
            direccion: b.direccion || "",
            casaDepto: b.casaDepto || "",
            celular: b.celular || "",
            accion: "EXISTENTE",
            estadoId: b.estadoId || 0,
            porcentaje: b.porcentaje,
            nombreCompleto:
              `${b.nombre} ${b.apellidoPaterno} ${b.apellidoMaterno}`.trim(),
            rutFormateado: `${b.rut}-${b.dv}`,
            descripcionEstado: b.descripcionEstado || "",
          }));

        const allBeneficiarios = [
          ...activeApiBeneficiarios,
          ...modified.map((b) => ({
            tipoBeneficiario: b.tipoBeneficiario,
            rut: b.rut,
            dv: b.dv,
            nombre: b.nombre,
            apellidoPaterno: b.apellidoPaterno,
            apellidoMaterno: b.apellidoMaterno,
            fechaNacimiento: b.fechaNacimiento,
            sexo: b.sexo || 0,
            parentesco: b.parentesco,
            email: b.email,
            region: b.region,
            ciudad: b.ciudad,
            comuna: b.comuna,
            direccion: b.direccion,
            casaDepto: b.casaDepto,
            celular: b.celular,
            accion: "MODIFICAR",
            estadoId: 0,
            porcentaje: b.porcentaje,
            nombreCompleto:
              `${b.nombre} ${b.apellidoPaterno} ${b.apellidoMaterno}`.trim(),
            rutFormateado: `${b.rut}-${b.dv}`,
            descripcionEstado: "",
          })),
          ...added.map((b) => ({
            tipoBeneficiario: b.tipoBeneficiario,
            rut: b.rut,
            dv: b.dv,
            nombre: b.nombre,
            apellidoPaterno: b.apellidoPaterno,
            apellidoMaterno: b.apellidoMaterno,
            fechaNacimiento: b.fechaNacimiento,
            sexo: b.sexo || 0,
            parentesco: b.parentesco,
            email: b.email,
            region: b.region,
            ciudad: b.ciudad,
            comuna: b.comuna,
            direccion: b.direccion,
            casaDepto: b.casaDepto,
            celular: b.celular,
            accion: "AGREGAR",
            estadoId: 0,
            porcentaje: b.porcentaje,
            nombreCompleto:
              `${b.nombre} ${b.apellidoPaterno} ${b.apellidoMaterno}`.trim(),
            rutFormateado: `${b.rut}-${b.dv}`,
            descripcionEstado: "",
          })),
        ];

        const beneficiariosEliminados = deleted.map((b) => {
          const apiData = apiBeneficiarios.find(
            (api: any) => api.rut === b.rut
          );

          return {
            tipoBeneficiario:
              b.tipoBeneficiario || apiData?.tipoBeneficiario || "PRIMARIO",
            rut: b.rut,
            dv: b.dv || apiData?.dv || "",
            nombre: b.nombre || apiData?.nombre || "",
            apellidoPaterno:
              b.apellidoPaterno || apiData?.apellidoPaterno || "",
            apellidoMaterno:
              b.apellidoMaterno || apiData?.apellidoMaterno || "",
            fechaNacimiento:
              b.fechaNacimiento ||
              apiData?.fechaNacimiento ||
              new Date().toISOString(),
            sexo: b.sexo || apiData?.sexo || 0,
            parentesco:
              b.parentesco ||
              (apiData?.parentesco ? Number.parseInt(apiData.parentesco) : 0),
            email: b.email || apiData?.email || "",
            region: b.region || apiData?.idRegion || 0,
            ciudad: b.ciudad || apiData?.idCiudad || 0,
            comuna: b.comuna || apiData?.idComuna || 0,
            direccion: b.direccion || apiData?.direccion || "",
            casaDepto: b.casaDepto || apiData?.casaDepto || "",
            celular: b.celular || apiData?.celular || "",
            accion: "ELIMINAR",
            estadoId: apiData?.estadoId || 0,
            porcentaje: b.porcentaje || apiData?.porcentaje || 0,
            nombreCompleto:
              b.nombreCompleto ||
              `${apiData?.nombre || b.nombre} ${
                apiData?.apellidoPaterno || b.apellidoPaterno
              } ${apiData?.apellidoMaterno || b.apellidoMaterno}`.trim(),
            rutFormateado:
              b.rutFormateado || `${b.rut}-${b.dv || apiData?.dv || ""}`,
            descripcionEstado: apiData?.descripcionEstado || "",
          };
        });

        return {
          NumeroPoliza: numeroPoliza,
          Beneficiarios: allBeneficiarios,
          BeneficiariosEliminados: beneficiariosEliminados,
        };
      }),
    };

    const saveResponse = await apiClient.saveBeneficiarios(beneficiariosData);

    if (!saveResponse.success) {
      setLoading(false);

      let errorList: ErrorByRut[] = [];

      if (saveResponse.errors && Array.isArray(saveResponse.errors)) {
        // Transformar los errores al formato deseado
        errorList = transformApiErrorsToRutGroups(saveResponse.errors);
      }

      setErrorMessage(
        saveResponse.message ||
          "Ha ocurrido un error al guardar los beneficiarios. Por favor, intenta nuevamente."
      );

      setErrorList(errorList);
      setShowErrorDialog(true);
      return;
    }

    const closeResponse = await apiClient.closeSolicitud({
      rut: asegurado.rut,
      solicitudGuid: solicitudGuid,
      otp: otp,
    });

    setLoading(false);

    if (closeResponse.success) {
      setSuccess(true);
    } else {
      setErrorMessage(
        closeResponse.message ||
          "Ha ocurrido un error al finalizar la solicitud. Por favor, verifica tu código OTP e intenta nuevamente."
      );
      setShowErrorDialog(true);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    const [year, month, day] = dateString.split("-").map(Number);
    if (!year || !month || !day) return "N/A";

    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("es-CL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getDeletedBeneficiarioFullData = (
    deletedBeneficiario: any,
    numeroPoliza: number
  ) => {
    if (
      deletedBeneficiario.nombre &&
      deletedBeneficiario.apellidoPaterno &&
      deletedBeneficiario.fechaNacimiento
    ) {
      return deletedBeneficiario;
    }

    const poliza = polizasData.find((p) => p.numeroPoliza === numeroPoliza);
    if (!poliza) return deletedBeneficiario;

    const apiBeneficiario = poliza.beneficiarios?.find(
      (b: any) => b.rut === deletedBeneficiario.rut
    );
    if (!apiBeneficiario) return deletedBeneficiario;

    return {
      ...deletedBeneficiario,
      nombre: deletedBeneficiario.nombre || apiBeneficiario.nombre,
      apellidoPaterno:
        deletedBeneficiario.apellidoPaterno || apiBeneficiario.apellidoPaterno,
      apellidoMaterno:
        deletedBeneficiario.apellidoMaterno || apiBeneficiario.apellidoMaterno,
      dv: deletedBeneficiario.dv || apiBeneficiario.dv,
      fechaNacimiento:
        deletedBeneficiario.fechaNacimiento || apiBeneficiario.fechaNacimiento,
      parentesco: deletedBeneficiario.parentesco || apiBeneficiario.parentesco,
      email: deletedBeneficiario.email || apiBeneficiario.email || "",
      celular: deletedBeneficiario.celular || apiBeneficiario.celular || "",
      direccion:
        deletedBeneficiario.direccion || apiBeneficiario.direccion || "",
      porcentaje: deletedBeneficiario.porcentaje || apiBeneficiario.porcentaje,
    };
  };

  const handleLogout = () => {
    navigate(`${baseUrl}/login`);

    setTimeout(() => {
      const { clearAuth } = useAuthStore.getState();
      const { clearCache } = usePolizasCacheStore.getState();
      const { clearBeneficiarios } = useBeneficiariosStore.getState();
      const { clearConsent } = useConsentStore.getState();

      clearAuth();
      clearCache();
      clearBeneficiarios();
      clearConsent();
      localStorage.setItem("beneficiarios", "");
    }, 100);
  };

  const totalErrors = errorList.reduce(
    (sum, group) => sum + group.errors.length,
    0
  );

  const toggleItem = (rut: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(rut)) {
      newExpanded.delete(rut);
    } else {
      newExpanded.add(rut);
    }
    setExpandedItems(newExpanded);
  };

  if (success) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar onLogoutClick={handleLogout} />
        <main className="flex flex-1 items-center justify-center bg-gradient-to-br from-[var(--color-dove)] to-[var(--color-z-white)] px-4">
          <Card className="w-full max-w-md text-center shadow-lg">
            <CardHeader>
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl text-[var(--color-z-blue)]">
                Solicitud Completada
              </CardTitle>
              <CardDescription className="text-base">
                <p className="pb-4">
                  Tù solicitud de beneficiarios ha sido procesada exitosamente.
                  Serás redirigido al inicio.
                </p>
                <Button
                  onClick={handleLogout}
                  className="bg-[var(--color-z-blue)] "
                >
                  Volver al inicio
                </Button>
              </CardDescription>
            </CardHeader>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar onLogoutClick={handleLogout} />
      <main className="flex-1 bg-gradient-to-br from-[var(--color-dove)] to-[var(--color-z-white)] px-4 py-8">
        <div className="mx-auto max-w-4xl">
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="mb-4 gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver
            </Button>
            <h1 className="text-3xl font-bold text-[var(--color-z-blue)]">
              Resumen de Solicitud
            </h1>
            <p className="mt-2 text-muted-foreground">
              Revisa y confirma los cambios antes de finalizar
            </p>
          </div>

          <Alert className="mb-6 border-[var(--color-z-blue)]/10 bg-[var(--color-z-blue)]/5 text-[var(--color-z-blue)]">
            <ShieldCheck className="h-5 w-5 text-[var(--color-z-blue)]" />
            <AlertDescription>
              <p className="font-semibold text-[var(--color-z-blue)] mb-2">
                {" "}
                Código de Verificación Requerido
              </p>
              <p className="text-sm text-muted-foreground">
                Para finalizar tu solicitud, necesitarás ingresar un código de
                verificación que fue enviado a tu correo electrónico
                {asegurado?.email && (
                  <span className="font-medium text-[var(--color-z-blue)]">
                    {" "}
                    {asegurado.email}
                  </span>
                )}
                . Por favor, ten a mano tu correo para completar el proceso de
                forma rápida y segura.
              </p>
            </AlertDescription>
          </Alert>

          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {!validation.isValid && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-semibold">
                    Las siguientes pólizas no cumplen con el 100% de cobertura:
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    {validation.invalidPolizas.map((p) => (
                      <li key={p.numero}>
                        Póliza N° {p.numero}: {p.total}% asignado
                        {p.faltante > 0
                          ? ` (falta ${p.faltante}%)`
                          : ` (excede por ${Math.abs(p.faltante)}%)`}
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`${baseUrl}/polizas`)}
                    className="mt-2 w-full "
                  >
                    Ir a Pólizas para Ajustar
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Detalle de Cambios</CardTitle>
              <CardDescription>
                Acá puedes visualizar todos los cambios que realizaste agrupados
                por póliza
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {polizasWithChanges.map((numeroPoliza) => {
                const added = addedBeneficiarios.filter(
                  (b) => b.numeroPoliza === numeroPoliza
                );
                const modified = modifiedBeneficiarios.filter(
                  (b) => b.numeroPoliza === numeroPoliza
                );
                const deleted = deletedBeneficiarios.filter(
                  (b) => b.numeroPoliza === numeroPoliza
                );

                return (
                  <div key={numeroPoliza}>
                    <div className="mb-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-[var(--color-z-blue)]" />
                        <h3 className="font-semibold text-[var(--color-z-blue)]">
                          Póliza N° {numeroPoliza}
                        </h3>
                      </div>
                      <div className="flex gap-2">
                        {added.length > 0 && (
                          <Badge variant="zblue">
                            <UserPlus className="h-3 w-3 mr-1" />
                            {added.length} Agregados
                          </Badge>
                        )}
                        {modified.length > 0 && (
                          <Badge variant="zblue">
                            <Edit className="h-3 w-3 mr-1" />
                            {modified.length} Modificados
                          </Badge>
                        )}
                        {deleted.length > 0 && (
                          <Badge variant="zblue">
                            <Trash2 className="h-3 w-3 mr-1" />
                            {deleted.length} Eliminados
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="mb-3 rounded-lg border-2 border-[var(--color-z-blue)]/20 bg-blue-50 p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-[var(--color-z-blue)]">
                          Cobertura Total:
                        </span>
                        <Badge
                          variant={(() => {
                            const poliza = polizasData.find(
                              (p) => p.numeroPoliza === numeroPoliza
                            );
                            if (!poliza) return "zblue";
                            const total = getTotalPercentageByPoliza(
                              numeroPoliza,
                              poliza.beneficiarios
                            );
                            if (total === 100) return "zblue";
                            if (total === 0) return "zblue";
                            if (total > 100) return "percentage-error";
                            return "zblue";
                          })()}
                        >
                          {(() => {
                            const poliza = polizasData.find(
                              (p) => p.numeroPoliza === numeroPoliza
                            );
                            if (!poliza) return "N/A";
                            const total = getTotalPercentageByPoliza(
                              numeroPoliza,
                              poliza.beneficiarios
                            );
                            return `${total}%`;
                          })()}
                        </Badge>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {added.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-2">
                            <UserPlus className="h-4 w-4" />
                            Beneficiarios Agregados
                          </h4>
                          <div className="space-y-2">
                            {added.map((beneficiario, index) => (
                              <div
                                key={index}
                                className="rounded-lg border-2 border-green-200 bg-green-50 p-4"
                              >
                                <div className="space-y-3">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <p className="font-semibold text-lg text-foreground">
                                        {beneficiario.nombre}{" "}
                                        {beneficiario.apellidoPaterno}{" "}
                                        {beneficiario.apellidoMaterno}
                                      </p>
                                      <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                        <span className="font-medium">
                                          RUT:
                                        </span>{" "}
                                        {formatRut(
                                          `${beneficiario.rut}${beneficiario.dv}`
                                        )}
                                      </p>
                                    </div>
                                    <Badge variant="added">
                                      Porcentaje {beneficiario.porcentaje}%
                                    </Badge>
                                  </div>

                                  <Separator />

                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                    <div className="flex items-center gap-2 text-muted-foreground">
                                      <Calendar className="h-4 w-4 text-[var(--color-z-blue)]" />
                                      <span className="font-medium">
                                        F. Nacimiento:
                                      </span>
                                      <span>
                                        {formatDate(
                                          beneficiario.fechaNacimiento
                                        )}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2 text-muted-foreground">
                                      <Users className="h-4 w-4 text-[var(--color-z-blue)]" />
                                      <span className="font-medium">
                                        Parentesco:
                                      </span>
                                      <span>
                                        {getParentescoLabel(
                                          beneficiario.parentesco
                                        )}
                                      </span>
                                    </div>
                                    {beneficiario.email && (
                                      <div className="flex items-center gap-2 text-muted-foreground">
                                        <Mail className="h-4 w-4 text-[var(--color-z-blue)]" />
                                        <span className="truncate">
                                          {beneficiario.email}
                                        </span>
                                      </div>
                                    )}
                                    {beneficiario.celular && (
                                      <div className="flex items-center gap-2 text-muted-foreground">
                                        <Phone className="h-4 w-4 text-[var(--color-z-blue)]" />
                                        <span>{beneficiario.celular}</span>
                                      </div>
                                    )}
                                    {beneficiario.direccion && (
                                      <div className="flex items-center gap-2 text-muted-foreground col-span-full">
                                        <MapPin className="h-4 w-4 text-[var(--color-z-blue)]" />
                                        <span className="truncate">
                                          {beneficiario.direccion}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {modified.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-2">
                            <Edit className="h-4 w-4" />
                            Beneficiarios Modificados
                          </h4>
                          <div className="space-y-2">
                            {modified.map((beneficiario, index) => (
                              <div
                                key={index}
                                className="rounded-lg border-2 border-amber-200 bg-amber-50 p-4"
                              >
                                <div className="space-y-3">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <p className="font-semibold text-lg text-foreground">
                                        {beneficiario.nombre}{" "}
                                        {beneficiario.apellidoPaterno}{" "}
                                        {beneficiario.apellidoMaterno}
                                      </p>
                                      <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                        <span className="font-medium">
                                          RUT:
                                        </span>{" "}
                                        {formatRut(
                                          `${beneficiario.rut}${beneficiario.dv}`
                                        )}
                                      </p>
                                    </div>
                                    <Badge variant="modified">
                                      Porcentaje {beneficiario.porcentaje}%
                                    </Badge>
                                  </div>

                                  <Separator />

                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                    <div className="flex items-center gap-2 text-muted-foreground">
                                      <Calendar className="h-4 w-4 text-[var(--color-z-blue)]" />
                                      <span className="font-medium">
                                        F. Nacimiento:
                                      </span>
                                      <span>
                                        {formatDate(
                                          beneficiario.fechaNacimiento
                                        )}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2 text-muted-foreground">
                                      <Users className="h-4 w-4 text-[var(--color-z-blue)]" />
                                      <span className="font-medium">
                                        Parentesco:
                                      </span>
                                      <span>
                                        {getParentescoLabel(
                                          beneficiario.parentesco
                                        )}
                                      </span>
                                    </div>
                                    {beneficiario.email && (
                                      <div className="flex items-center gap-2 text-muted-foreground">
                                        <Mail className="h-4 w-4 text-[var(--color-z-blue)]" />
                                        <span className="truncate">
                                          {beneficiario.email}
                                        </span>
                                      </div>
                                    )}
                                    {beneficiario.celular && (
                                      <div className="flex items-center gap-2 text-muted-foreground">
                                        <Phone className="h-4 w-4 text-[var(--color-z-blue)]" />
                                        <span>{beneficiario.celular}</span>
                                      </div>
                                    )}
                                    {beneficiario.direccion && (
                                      <div className="flex items-center gap-2 text-muted-foreground col-span-full">
                                        <MapPin className="h-4 w-4 text-[var(--color-z-blue)]" />
                                        <span className="truncate">
                                          {beneficiario.direccion}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {deleted.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-2">
                            <Trash2 className="h-4 w-4" />
                            Beneficiarios Eliminados
                          </h4>
                          <div className="space-y-2">
                            {deleted.map((deletedBeneficiario, index) => {
                              const beneficiario =
                                getDeletedBeneficiarioFullData(
                                  deletedBeneficiario,
                                  numeroPoliza
                                );

                              return (
                                <div
                                  key={index}
                                  className="rounded-lg border-2 border-red-200 bg-red-50 p-4"
                                >
                                  <div className="space-y-3">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <p className="font-semibold text-lg text-foreground">
                                          {beneficiario.nombre || "Sin nombre"}{" "}
                                          {beneficiario.apellidoPaterno || ""}{" "}
                                          {beneficiario.apellidoMaterno || ""}
                                        </p>
                                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                          <span className="font-medium">
                                            RUT:
                                          </span>{" "}
                                          {formatRut(
                                            `${beneficiario.rut}${
                                              beneficiario.dv || ""
                                            }`
                                          )}
                                        </p>
                                      </div>
                                      <Badge variant="deleted">
                                        {beneficiario.porcentaje
                                          ? `Porcentaje ${beneficiario.porcentaje}%`
                                          : "Eliminado"}
                                      </Badge>
                                    </div>

                                    {(beneficiario.fechaNacimiento ||
                                      beneficiario.parentesco ||
                                      beneficiario.email ||
                                      beneficiario.celular ||
                                      beneficiario.direccion) && (
                                      <>
                                        <Separator />

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                          {beneficiario.fechaNacimiento && (
                                            <div className="flex items-center gap-2 text-muted-foreground">
                                              <Calendar className="h-4 w-4 text-[var(--color-z-blue)]" />
                                              <span className="font-medium">
                                                F. Nacimiento:
                                              </span>
                                              <span>
                                                {formatDate(
                                                  beneficiario.fechaNacimiento
                                                )}
                                              </span>
                                            </div>
                                          )}
                                          {beneficiario.parentesco !==
                                            undefined &&
                                            beneficiario.parentesco !== 0 && (
                                              <div className="flex items-center gap-2 text-muted-foreground">
                                                <Users className="h-4 w-4 text-[var(--color-z-blue)]" />
                                                <span className="font-medium">
                                                  Parentesco:
                                                </span>
                                                <span>
                                                  {getParentescoLabel(
                                                    beneficiario.parentesco
                                                  )}
                                                </span>
                                              </div>
                                            )}
                                          {beneficiario.email && (
                                            <div className="flex items-center gap-2 text-muted-foreground">
                                              <Mail className="h-4 w-4 text-[var(--color-z-blue)]" />
                                              <span className="truncate">
                                                {beneficiario.email}
                                              </span>
                                            </div>
                                          )}
                                          {beneficiario.celular && (
                                            <div className="flex items-center gap-2 text-muted-foreground">
                                              <Phone className="h-4 w-4 text-[var(--color-z-blue)]" />
                                              <span>
                                                {beneficiario.celular}
                                              </span>
                                            </div>
                                          )}
                                          {beneficiario.direccion && (
                                            <div className="flex items-center gap-2 text-muted-foreground col-span-full">
                                              <MapPin className="h-4 w-4 text-[var(--color-z-blue)]" />
                                              <span className="truncate">
                                                {beneficiario.direccion}
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-[var(--color-z-blue)]">
                Finalizar Solicitud
              </CardTitle>
              <CardDescription className="text-xs text-[var(--color-z-blue)]/80">
                Confirma los cambios para finalizar la solicitud
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-z-blue)]/10">
                      <ShieldCheck className="h-5 w-5 text-[var(--color-z-blue)]" />
                    </div>
                    <div>
                      <label
                        htmlFor="otp"
                        className="text-sm font-semibold text-[var(--color-z-blue)]"
                      >
                        Código de Verificación (OTP)
                      </label>
                      <p className="text-xs text-[var(--color-z-blue)]/80">
                        Revisa tu correo{" "}
                        {asegurado?.email && (
                          <span className="font-medium text-[var(--color-z-blue)]">
                            {asegurado.email}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                      <Mail className="h-5 w-5 text-[var(--color-z-blue)]" />
                    </div>
                    <input
                      id="otp"
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      className="w-full rounded-lg border-2 border-[var(--color-z-blue)]/20 bg-white px-12 py-4 text-center text-2xl font-semibold tracking-widest text-[var(--color-z-blue)] placeholder:text-muted-foreground/40 focus:border-[var(--color-z-blue)] focus:outline-none focus:ring-4 focus:ring-[var(--color-z-blue)]/10 transition-all"
                      placeholder="000000"
                      maxLength={6}
                      autoComplete="one-time-code"
                    />
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                      {otp.length === 6 ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                      ) : (
                        <div className="text-xs font-medium text-muted-foreground">
                          {otp.length}/6
                        </div>
                      )}
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground text-center">
                    Ingresa el código de 6 dígitos que recibiste por correo
                    electrónico
                  </p>
                </div>
                {/* </CHANGE> */}

                <Button
                  type="submit"
                  className="w-full bg-[var(--color-z-blue)] hover:bg-[var(--color-z-blue-dark)] h-12 text-base font-semibold"
                  disabled={loading || !validation.isValid || !otp}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2 h-5 w-5" />
                      Finalizar Solicitud
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />

      <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <DialogContent className="sm:max-w-1xl md:max-w-2xl lg:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[var(--color-z-blue)] sm:text-lg">
              {"Errores "}
            </DialogTitle>
            <DialogDescription className="relative p-6 pb-4">
              <div className="">
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                    <AlertCircle className="w-8 h-8 text-[var(--color-z-blue)]" />
                  </div>

                  <h2 className="text-2xl font-semibold text-slate-800 mb-2">
                    Ups, algo no salió según lo previsto
                  </h2>

                  <p className="text-slate-600 text-sm">
                    Estamos revisando lo ocurrido. Por favor, intenta nuevamente
                    o verifica los datos ingresados.
                  </p>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full pointer-events-auto animate-in zoom-in-95 duration-200 sm:max-w-1xl md:max-w-2xl lg:max-w-2xl">
            <div className="bg-slate-50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="bg-red-100 text-red-700 text-xs font-medium px-2.5 py-1 rounded-full">
                  {errorMessage}
                </span>
              </div>

              {/* Accordion */}
              <div className="space-y-2">
                {errorList.map((group) => {
                  const isExpanded = expandedItems.has(group.rut);

                  return (
                    <div
                      key={group.rut}
                      className="bg-white rounded-lg border border-slate-200 overflow-hidden transition-all duration-200"
                    >
                      <button
                        onClick={() => toggleItem(group.rut)}
                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-red-600 font-semibold text-sm">
                              {group.errors.length}
                            </span>
                          </div>
                          <span className="font-mono text-sm font-medium text-slate-700">
                            RUT {group.rut}
                          </span>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </button>

                      {isExpanded && (
                        <div className="px-4 pb-3 animate-in slide-in-from-top-2 duration-200">
                          <div className="pl-11 space-y-2">
                            {group.errors.map((error, index) => (
                              <div
                                key={index}
                                className="text-sm text-slate-600 flex items-start gap-2"
                              >
                                <span className="text-red-500 mt-1">•</span>
                                <span className="flex-1">{error}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div>
            <p className="text-center text-sm text-red-600 mt-4">
              Si el problema persiste, contacta al soporte.
            </p>
          </div>
          {/* Footer */}
          <div className="px-12">
            <Button
              onClick={() => setShowErrorDialog(false)}
              className="w-full  text-white font-medium py-3 rounded-lg transition-colors bg-[var(--color-z-blue)] hover:bg-[var(--color-z-blue-dark)]"
            >
              Entendido
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
